import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import usePersistedPagination from "./usePersistedPagination";
import { DEFAULT_PAGE_SIZE } from "../components/RewampPagination";

export const PAGINATION_PLACEMENT = Object.freeze({
  shell: "shell",
  content: "content",
  none: "none",
});

// spec.shell.pagination shape:
//   { key: string | (location) => string, entryId?: string | (location) => string,
//     basePageSize?: number, sizeControl?: boolean, placement?: "shell"|"content"|"none",
//     minPages?: number }   // hide the pager below this many pages (default 1 = show when any results)
export default function usePagePagination(spec) {
  const location = useLocation();
  const resolve = (v) => (typeof v === "function" ? v(location) : v);
  const key = resolve(spec?.key) ?? `page:${location.pathname}`;
  const entryId = resolve(spec?.entryId);
  const basePageSize = spec?.basePageSize ?? DEFAULT_PAGE_SIZE;
  const sizeControl = spec?.sizeControl ?? true;
  const placement = spec?.placement ?? PAGINATION_PLACEMENT.shell;
  const minPages = spec?.minPages ?? 1;

  const { page, pageSize, setPage, setPageSize } = usePersistedPagination(key, {
    defaultPageSize: basePageSize,
    entryId,
  });
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Server-paged hooks return {totalPages,totalCount}; client-paged bodies compute them. Same entry point.
  const publishCounts = useCallback((next) => {
    if (next?.totalPages != null || next?.pageCount != null) setPageCount(next.totalPages ?? next.pageCount);
    if (next?.totalCount != null) setTotalCount(next.totalCount);
  }, []);

  const shellProps = useMemo(() => {
    if (placement !== PAGINATION_PLACEMENT.shell || totalCount <= 0 || pageCount < minPages) return null;
    return {
      count: pageCount,
      page,
      onChange: setPage,
      pageSize,
      basePageSize,
      totalCount,
      ...(sizeControl ? { onPageSizeChange: setPageSize } : {}),
    };
  }, [placement, totalCount, pageCount, minPages, page, setPage, pageSize, basePageSize, sizeControl, setPageSize]);

  return {
    page, pageSize, setPage, setPageSize,
    pageCount, totalCount, publishCounts,
    setPageCount, setTotalCount,
    placement,
    shellProps,
  };
}
